import type { SyncConflictRow } from '@infra/sync/RemoteSyncStore';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { RemoteSyncStore } from '@infra/sync/RemoteSyncStore';

export type ConflictChoice = 'local' | 'remote';

/** Ressource bulletin — conflits moteur (engine_hash) à traiter en priorité. */
export function isBulletinSyncResource(resource: string): boolean {
  return resource === 'bulletins';
}

/** Présente un choix à l'utilisateur et applique la résolution sur le serveur. */
export async function resolveSyncConflict(conflict: SyncConflictRow): Promise<boolean> {
  const choice = await promptConflictChoice(conflict);
  if (!choice) return false;

  const api = getBusinessApiClient();
  if (!(await api.confirmWriteReady()) || !api.hasUserSession()) return false;

  if (choice === 'remote') {
    await RemoteSyncStore.putCache(
      conflict.tenant_id,
      conflict.resource,
      conflict.entity_id,
      conflict.remote_payload,
      conflict.remote_version,
      false,
    );
    return true;
  }

  const row = await api.updateEntity(
    conflict.resource,
    conflict.entity_id,
    conflict.local_payload,
    conflict.remote_version,
  );
  await RemoteSyncStore.putCache(
    conflict.tenant_id,
    conflict.resource,
    row.id,
    row.payload,
    row.version,
    false,
  );
  return true;
}

function summarizePayload(payload: Record<string, unknown>): string {
  const keys = Object.keys(payload).slice(0, 6);
  return keys.map((k) => `${k}: ${String(payload[k]).slice(0, 40)}`).join('\n');
}

async function promptConflictChoice(conflict: SyncConflictRow): Promise<ConflictChoice | null> {
  const msg = [
    `Conflit de synchronisation — ${conflict.resource} (${conflict.entity_id})`,
    '',
    '— Version locale (hors ligne) —',
    summarizePayload(conflict.local_payload),
    '',
    '— Version serveur —',
    summarizePayload(conflict.remote_payload),
    '',
    'OK = garder la version LOCALE sur le serveur.',
    'Annuler = garder la version SERVEUR sur ce poste.',
  ].join('\n');

  const keepLocal = window.confirm(msg);
  return keepLocal ? 'local' : 'remote';
}
